import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { AddressForm } from '../components/AddressForm'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { OrderSummary } from '../components/OrderSummary'
import { useCart } from '../hooks/useCart'
import { useCheckout } from '../hooks/useCheckout'
import type { ShopAddress } from '../types'
import { getStripePromise } from '@/lib/stripe'
import { formatUserFacingError } from '@/lib/errors'

// ── Inner payment form (rendered inside <Elements>) ───────────────────────────

interface PaymentFormProps {
  orderId: string
  receiptToken: string
  onSuccess: (orderId: string, receiptToken: string) => void
}

function PaymentForm({ orderId, receiptToken, onSuccess }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/shop/order-success?${new URLSearchParams({
          orderId,
          receiptToken,
        }).toString()}`,
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(formatUserFacingError(
        stripeError,
        'We could not complete your payment. Please check your details and try again.',
        'payments/shop-confirmation',
      ))
      setProcessing(false)
    } else {
      onSuccess(orderId, receiptToken)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="font-semibold">Payment</h2>
      <PaymentElement />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <HoldConfirmButton
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-brand-orange text-brand-black"
      >
        {processing ? 'Processing…' : 'Place Order'}
      </HoldConfirmButton>
    </form>
  )
}

// ── Checkout page ─────────────────────────────────────────────────────────────

type Step = 'address' | 'payment'

export function CheckoutPage() {
  const { items, clearCart } = useCart()
  const navigate = useNavigate()
  const { clientSecret, orderId, receiptToken, loading, error, initiateCheckout } = useCheckout()
  const [step, setStep] = useState<Step>('address')

  // Warn before leaving once we've opened a PaymentIntent but the order isn't
  // placed yet. Prevents accidental back-navigation/reload from orphaning the
  // Stripe intent. Modern browsers show a generic dialog regardless of string.
  useEffect(() => {
    if (step !== 'payment' || !clientSecret) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [step, clientSecret])

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground">Your cart is empty.</p>
        <Link to="/shop" className="mt-4 inline-block text-sm hover:underline">
          ← Back to shop
        </Link>
      </div>
    )
  }

  async function handleAddressSubmit(shipping: ShopAddress) {
    await initiateCheckout(items, shipping)
    setStep('payment')
  }

  function handleOrderSuccess(completedOrderId: string, completedReceiptToken: string) {
    clearCart()
    const params = new URLSearchParams({
      orderId: completedOrderId,
      receiptToken: completedReceiptToken,
    })
    navigate(`/shop/order-success?${params.toString()}`)
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to shop
      </Link>

      <h1 className="mb-8 text-2xl font-bold">Checkout</h1>

      <div className="grid gap-8 md:grid-cols-[1fr_360px]">
        <div className="space-y-8">
          {step === 'address' && (
            <div>
              <h2 className="mb-4 font-semibold">Shipping Address</h2>
              <AddressForm
                onSubmit={handleAddressSubmit}
                isSubmitting={loading}
                submitLabel="Continue to Payment"
              />
              {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
            </div>
          )}

          {step === 'payment' && clientSecret && orderId && receiptToken && (
            <Elements
              stripe={getStripePromise()}
              options={{
                clientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <PaymentForm
                orderId={orderId}
                receiptToken={receiptToken}
                onSuccess={handleOrderSuccess}
              />
            </Elements>
          )}

          {step === 'payment' && !clientSecret && (
            <p className="text-sm text-muted-foreground">Loading payment form…</p>
          )}
        </div>

        <div>
          <OrderSummary items={items} />
        </div>
      </div>
    </div>
  )
}
